import { Shield, FileText, Lock, AlertTriangle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

const PrivacyPage = () => {
  const { t, language } = useLanguage();
  const { isDark } = useTheme();

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0A0A0A]' : 'bg-gray-50'}`}>
      <SEO 
        title={language === 'en' ? 'Privacy Policy & Disclaimer' : 'গোপনীয়তা নীতি ও দায়মুক্তি'}
        description={language === 'en'
          ? 'Read Bhumi\'s privacy policy, terms of service, and legal disclaimer. Understand how we handle your data and our service limitations.'
          : 'ভূমির গোপনীয়তা নীতি, সেবার শর্তাবলী এবং আইনি দায়মুক্তি পড়ুন। আমরা কীভাবে আপনার তথ্য পরিচালনা করি তা জানুন।'
        }
        path="/privacy"
        noIndex={false}
      />
      <Navbar isDark={isDark} />

      {/* Header */}
      <section className={`pt-24 pb-12 border-b ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'}`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${isDark ? 'bg-[#2E7D32]/20' : 'bg-[#E8F5E9]'}`}>
              <Shield className="text-[#2E7D32]" size={32} />
            </div>
            <h1 className={`text-3xl sm:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="privacy-title">
              {t('privacy.title')}
            </h1>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t('privacy.lastUpdated')}: ডিসেম্বর ২০২৪</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Disclaimer */}
          <div className={`rounded-xl p-6 mb-8 border ${isDark ? 'bg-[#FBC02D]/10 border-[#FBC02D]/30' : 'bg-[#FFFDE7] border-[#FBC02D]'}`}>
            <div className="flex items-start gap-4">
              <AlertTriangle className="text-[#F9A825] flex-shrink-0 mt-1" size={24} />
              <div>
                <h2 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('privacy.importantNotice')}</h2>
                <p className={isDark ? 'text-gray-300' : 'text-gray-700'} data-testid="main-disclaimer">
                  <strong>ভূমি কোনো জমি কেনা-বেচা বা আইনি নিশ্চয়তা প্রদান করে না।</strong> আমরা 
                  তথ্য ও সংযোগের একটি প্ল্যাটফর্ম। জমি সংক্রান্ত যেকোনো আইনি সিদ্ধান্ত নেওয়ার আগে 
                  অবশ্যই যোগ্য আইনজীবীর পরামর্শ নিন।
                </p>
              </div>
            </div>
          </div>

          {/* Privacy Policy */}
          <div className={`rounded-xl p-6 sm:p-8 border mb-8 ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-3 mb-6">
              <Lock className="text-[#2E7D32]" size={24} />
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('privacy.privacyPolicy')}</h2>
            </div>
            
            <div className={`space-y-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <div>
                <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>তথ্য সংগ্রহ</h3>
                <p>
                  আমরা আপনার প্রদত্ত তথ্য যেমন নাম, ইমেইল, ফোন নম্বর এবং বার্তা সংগ্রহ করি। 
                  এই তথ্য শুধুমাত্র আপনার সাথে যোগাযোগ এবং সেবা প্রদানের জন্য ব্যবহৃত হয়।
                </p>
              </div>

              <div>
                <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>তথ্য ব্যবহার</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>আপনার অনুরোধে সাড়া দেওয়া</li>
                  <li>সেবাদাতাদের সাথে সংযোগ করা</li>
                  <li>প্ল্যাটফর্মের উন্নতি করা</li>
                  <li>গুরুত্বপূর্ণ আপডেট পাঠানো</li>
                </ul>
              </div>

              <div>
                <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>তথ্য সুরক্ষা</h3>
                <p>
                  আমরা আপনার তথ্য সুরক্ষিত রাখতে উপযুক্ত প্রযুক্তিগত ও সাংগঠনিক ব্যবস্থা গ্রহণ করি। 
                  তবে, ইন্টারনেটে ১০০% সুরক্ষার নিশ্চয়তা দেওয়া সম্ভব নয়।
                </p>
              </div>

              <div>
                <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>তথ্য শেয়ারিং</h3>
                <p>
                  আমরা আপনার অনুমতি ছাড়া তৃতীয় পক্ষের সাথে আপনার ব্যক্তিগত তথ্য শেয়ার করি না, 
                  আইনি বাধ্যবাধকতা ছাড়া।
                </p>
              </div>
            </div>
          </div>

          {/* Terms of Service */}
          <div className={`rounded-xl p-6 sm:p-8 border mb-8 ${isDark ? 'bg-[#111] border-white/10' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center gap-3 mb-6">
              <FileText className="text-[#2E7D32]" size={24} />
              <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('privacy.termsOfService')}</h2>
            </div>
            
            <div className={`space-y-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              <div>
                <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>সেবার বিবরণ</h3>
                <p>
                  ভূমি একটি তথ্য ও সংযোগ প্ল্যাটফর্ম যা জমি সংক্রান্ত তথ্য প্রদান করে এবং 
                  ব্যবহারকারীদের যাচাইকৃত সেবাদাতাদের সাথে সংযুক্ত করে।
                </p>
              </div>

              <div>
                <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>দায় সীমাবদ্ধতা</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>আমরা কোনো আইনি পরামর্শ প্রদান করি না</li>
                  <li>সেবাদাতাদের কাজের জন্য আমরা দায়ী নই</li>
                  <li>তথ্যের সঠিকতার চূড়ান্ত দায় ব্যবহারকারীর</li>
                  <li>জমি লেনদেনে আমরা মধ্যস্থতাকারী নই</li>
                </ul>
              </div>

              <div>
                <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>ব্যবহারকারীর দায়িত্ব</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>সঠিক তথ্য প্রদান করা</li>
                  <li>প্ল্যাটফর্মের অপব্যবহার না করা</li>
                  <li>অন্যদের গোপনীয়তা সম্মান করা</li>
                  <li>আইনি পরামর্শের জন্য যোগ্য পেশাদারের শরণাপন্ন হওয়া</li>
                </ul>
              </div>

              <div>
                <h3 className={`font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>পরিবর্তন</h3>
                <p>
                  আমরা যেকোনো সময় এই শর্তাবলী পরিবর্তন করতে পারি। গুরুত্বপূর্ণ পরিবর্তনের 
                  ক্ষেত্রে আমরা ব্যবহারকারীদের অবহিত করব।
                </p>
              </div>
            </div>
          </div>

          {/* Contact for Privacy */}
          <div className={`rounded-xl p-6 text-center ${isDark ? 'bg-[#2E7D32]/10' : 'bg-[#E8F5E9]'}`}>
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('privacy.haveQuestions')}</h3>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              গোপনীয়তা বা শর্তাবলী সম্পর্কে কোনো প্রশ্ন থাকলে আমাদের সাথে যোগাযোগ করুন।
            </p>
            <a href="/contact">
              <button className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white px-6 py-2 rounded-full font-medium transition-colors">
                {t('nav.contact')}
              </button>
            </a>
          </div>
        </div>
      </section>

      <Footer isDark={isDark} />
    </div>
  );
};

export default PrivacyPage;
